import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  HostListener,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { ErrorOverlayRef } from '../models/error-overlay-ref.model';
import { ERROR_OVERLAY_DATA } from '../models/error-overlay-config.interface';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { ReportOverlayDynamicService } from '@app/overlays/report-overlay/services/report-overlay-dynamic.service';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { Params } from '@angular/router';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import {
  logErrorDialogShown,
  logTabbyPaymentRejected,
} from '@app/core/services/google-analytics.lazy';

import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { LottieAnimationComponent } from '@app/components/lottie-animation/lottie-animation.component';
import { AkitaCheckoutQuery } from '@app/akita/api/checkout/state/checkout.query';
import { AkitaFirebaseQuery } from '@app/akita/firebase/state/firebase.query';

@Component({
  selector: 'app-error-overlay',
  templateUrl: './error-overlay.template.html',
  styleUrls: ['./error-overlay.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, RemoveIconComponent, LottieAnimationComponent],
})
export class ErrorOverlayComponent implements OnDestroy {
  public rtlMode: boolean;
  public lessThan$: Observable<ScreenSizeInfoState>;

  public sessionID: string;
  public errorReason: string;
  public errorTitle: string;
  public errorMessage: string;
  public extra: Params;
  public showReportButton: boolean;

  public lang: string;
  public isStcPayErrorCode = false;

  // Report Overlay
  public reportOverlayOpened?: boolean | null;
  private reportOverlayObserver?: Subscription | null;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly errorOverlayRef: ErrorOverlayRef,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly analytics: GoogleAnalyticsService,
    private readonly reportOverlayService: ReportOverlayDynamicService,
    private readonly akitaFirebaseQuery: AkitaFirebaseQuery,
    private readonly akitaCheckoutQuery: AkitaCheckoutQuery,
    private readonly translate: TranslocoService,
    @Inject(ERROR_OVERLAY_DATA) private readonly data: Record<string, any>
  ) {
    this.destroySubject = new Subject();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.rtlMode = this.data.rtl;
    this.errorReason = this.data.reason;
    this.errorTitle = this.data.title;
    this.errorMessage = this.data.message;
    this.sessionID = `${this.akitaFirebaseQuery.sessionId}`;
    this.extra = {
      ...(this.data.extra || {}),
      'Session ID': `${this.akitaFirebaseQuery.sessionId}`,
      'Error Reason': `${this.errorReason || ''}`,
    };
    logErrorDialogShown(
      this.analytics.logEventWrapper,
      `${this.data.title || ''}`,
      `${this.data.message || ''}`,
      `${this.akitaFirebaseQuery.user?.id || 'anonymous'}`,
      `${this.akitaFirebaseQuery.sessionId}`
    );
    this.showReportButton = Boolean(this.data.showReportButton);

    this.lang = this.translate.getActiveLang();
    this.checkStcPayErrorCode();
  }

  @HostListener('document:keydown.escape', [])
  public handleKeydown(): void {
    this.dismiss();
  }

  private checkStcPayErrorCode(): void {
    if (this.errorTitle === '20120') {
      if (this.lang === 'ar') {
        this.errorTitle = 'العميل/المستخدم غير صالح';
        this.errorMessage =
          'يرجى التحقق من رقم هاتفك المحمول المسجل لدى STC Pay ثم المحاولة مرة أخرى';
      } else {
        this.errorTitle = 'Invalid customer/user';
        this.errorMessage =
          'Please verify your mobile number registered with STC Pay and try again';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '20051') {
      if (this.lang === 'ar') {
        this.errorTitle = 'رصيد غير كاف';
        this.errorMessage = 'رفض الدفع - حساب STC Pay لا يحتوي على أموال كافية';
      } else {
        this.errorTitle = 'Insufficient funds';
        this.errorMessage =
          'Payment declined - STC Pay account does not have sufficient funds';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '20151') {
      if (this.lang === 'ar') {
        this.errorTitle = 'OTP غير صحيح';
        this.errorMessage = 'يرجى طلب إرسال OTP جديد إلى رقم هاتفك المسجل';
      } else {
        this.errorTitle = 'OTP is not correct';
        this.errorMessage =
          'Please request a new OTP to be sent to your registered phone number';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '20019') {
      if (this.lang === 'ar') {
        this.errorTitle = 'انتهت صلاحية المعاملة';
        this.errorMessage =
          'يرجى التحقق من رقم هاتفك المحمول المسجل لدى STC Pay ثم المحاولة مرة أخرى';
      } else {
        this.errorTitle = 'Transaction has expired';
        this.errorMessage =
          'Please verify your mobile number registered with STC Pay and try again';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '99999') {
      if (this.lang === 'ar') {
        this.errorTitle = 'STC PAY غير متاح';
        this.errorMessage = 'يرجى المحاولة مرة أخرى لاحقًا أو اختيار طريقة دفع مختلفة';
      } else {
        this.errorTitle = 'STC PAY is unavailable';
        this.errorMessage = 'Please try again later or pick a different payment method';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2018') {
      if (this.lang === 'en') {
        this.errorMessage = 'Only Mobile Numbers supported';
      } else {
        this.errorMessage = 'فقط الأرقام المحمولة مدعومة';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2008') {
      if (this.lang === 'en') {
        this.errorMessage = 'Customer not found';
      } else {
        this.errorMessage = 'العميل غير موجود';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2028') {
      if (this.lang === 'en') {
        this.errorMessage = 'Please complete your Customer Information';
      } else {
        this.errorMessage = 'يرجى استكمال معلومات العميل';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2029') {
      if (this.lang === 'ar') {
        this.errorMessage = 'حالة حسابك غير صالحة لإجراء هذه المعاملة';
      } else {
        this.errorMessage =
          'Your account status is not valid to perform this transaction';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2031') {
      if (this.lang === 'ar') {
        this.errorMessage = 'قيمة OTP غير صالحة';
      } else {
        this.errorMessage = 'Invalid OTP value';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '1221') {
      if (this.lang === 'ar') {
        this.errorMessage =
          'الحد الأدنى للوقت بين OTPs هو 60 ثانية. حاول مرة أخرى بعد دقيقة واحدة.';
      } else {
        this.errorMessage =
          'Minimum time between OTPs is 60 sc. Try again after 1 minute.';
      }
      this.isStcPayErrorCode = true;
    } else if (this.errorTitle === '2003') {
      if (this.lang === 'ar') {
        this.errorMessage = 'رصيد غير كاف';
      } else {
        this.errorMessage = 'Insufficient funds';
      }
      this.isStcPayErrorCode = true;
    }

    if (this.errorMessage.includes('Invalid Mobile Number format')) {
      if (this.lang === 'ar') {
        this.errorMessage = 'صيغة رقم الهاتف غير صالحة';
      } else {
        this.errorMessage = 'Invalid Mobile Number format';
      }
    }

    if (this.errorTitle === 'INVALID_REQUEST') {
      if (this.lang === 'ar') {
        this.errorTitle = 'لقد حدث خطأ ما';
      } else {
        this.errorTitle = 'Something went wrong';
      }
    }

    if (this.errorMessage.includes('TABBY_PAYMENT_REJECTED')) {
      logTabbyPaymentRejected(
        this.analytics.logEventWrapper,
        this.akitaCheckoutQuery.session?.totalPrice?.amount || 0,
        this.akitaCheckoutQuery.session?.totalPrice?.currency || 'SAR'
      );

      if (this.lang === 'ar') {
        this.errorTitle = 'تجاوز الحد الائتماني';
        this.errorMessage =
          'لإكمال طلبك، جرب استخدام طريقة دفع أخرى أو تواصل مع تابي لطلب زيادة الحد الائتماني';
      } else {
        this.errorTitle = 'Exceeded Credit Limit';
        this.errorMessage =
          'To complete your order, try using a different payment method or contact tabby for a limit increase.';
      }
    }
  }

  public dismiss(): boolean {
    this.errorOverlayRef.close();
    return false;
  }

  public toggleReportDialog(): boolean {
    if (!this.reportOverlayOpened) {
      this.dismiss();

      if (this.reportOverlayObserver) {
        this.reportOverlayObserver.unsubscribe();
      }

      this.reportOverlayObserver = this.reportOverlayService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.reportOverlayOpened = Boolean(data);

            SentryUtil.addBreadcrumb({
              category: 'ui',
              message: `${
                this.reportOverlayOpened ? 'Opened' : 'Closed'
              } "Report" - Error Dialog`,
              level: 'info',
              type: 'user',
            });

            this.changeDetector.markForCheck();
          },
        });

      this.reportOverlayService.open({
        elementRef: null,
        data: {
          mode: 'feedback',
          extra: this.extra,
          rtlMode: this.rtlMode,
        },
      });
    }

    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
